import { uniq } from 'es-toolkit';
import React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { AlertColor } from '@mui/material';

import { createSnackbar, NotificationActionButton, Snackbar, VariantType } from '@paragon/notification-tools';
import { assertExhaustiveSwitch, sortBy } from '@paragon/utilities';

import { useAppDispatch } from '../../components/hooks';
import { useAppMonitor } from '../../monitor';
import { RootState } from '..';
import { finishCuttingCeilingPlane } from '../actions/layout/ceilingPlaneActions';
import { finishCuttingRoofPlane } from '../actions/layout/roofPlaneActions';
import { finishCuttingEnvelope } from '../actions/layout/trussEnvelopeActions';
import { clearPickingObject, toggleMemberCuttingMode } from '../slices/applicationSlice';
import { getActiveAnalysisSet } from './getActiveAnalysisSetSelector';
import { isLayoutViewActive } from './layoutModeSelectors';
import { getPickingObjectInfo } from './pickingSelectors';
import { getTrussEnvelopeErrorForSelectedEnvelopes } from './trussEnvelopeErrorSelectors';

function FinishPlaneCuttingButton({ mode }: { mode: 'roofPlane' | 'ceilingPlane' | 'trussEnvelope' }) {
  const dispatch = useAppDispatch();

  return (
    <NotificationActionButton
      onClick={() =>
        dispatch(
          mode === 'roofPlane'
            ? finishCuttingRoofPlane()
            : mode === 'ceilingPlane'
              ? finishCuttingCeilingPlane()
              : mode === 'trussEnvelope'
                ? finishCuttingEnvelope()
                : assertExhaustiveSwitch(mode),
        )
      }
    >
      Finish Cutting
    </NotificationActionButton>
  );
}

function FinishMemberCuttingButton() {
  const dispatch = useAppDispatch();

  return (
    <NotificationActionButton onClick={() => dispatch(toggleMemberCuttingMode())}>
      Finish Cutting
    </NotificationActionButton>
  );
}

function FinishEnvelopeTrimmingButton() {
  const dispatch = useAppDispatch();

  return <NotificationActionButton onClick={() => dispatch(clearPickingObject())}>Finish</NotificationActionButton>;
}

function DocumentationLinkButton({ url }: { url: string }) {
  const monitor = useAppMonitor();

  return (
    <NotificationActionButton
      href={url}
      /** @ts-expect-error "Property 'target' does not exist on type" */
      target="_blank"
      onClick={(event) => {
        monitor.recordInteraction('Documentation Link Opened', { url });
        event.stopPropagation();
      }}
    >
      Get Help ↗
    </NotificationActionButton>
  );
}

type SelectorSnackbar = [Snackbar[0], Omit<Snackbar[1], 'addedAt' | 'persist'>];

const getSelectorBasedSnackbars = createSelector(
  isLayoutViewActive,
  getPickingObjectInfo,
  (state: RootState) => state.application.inMemberCuttingMode,
  getActiveAnalysisSet,
  getTrussEnvelopeErrorForSelectedEnvelopes,
  (inLayoutView, pickingObjectInfo, inMemberCuttingMode, analysisSet, trussEnvelopeError): Snackbar[] => {
    const result: SelectorSnackbar[] = [];

    if (pickingObjectInfo?.type === 'pickingPlane') {
      let mode = null;
      switch (pickingObjectInfo.targetInfo.type) {
        case 'ceiling-cut':
          mode = 'ceilingPlane' as const;
          break;
        case 'roof-cut':
          mode = 'roofPlane' as const;
          break;
        case 'truss-envelope-ordered-top-cut':
        case 'truss-envelope-ordered-bottom-cut':
          mode = 'trussEnvelope' as const;
          break;
      }

      if (mode != null) {
        result.push([
          'Pick plane(s) to cut against (hold Shift to cut against any line vertically).',
          {
            key: 'static-pick-plane',
            variant: 'info',
            interactive: true,
            action: () => <FinishPlaneCuttingButton mode={mode} />,
          },
        ]);
      }
    }

    if (
      inLayoutView &&
      pickingObjectInfo?.type === 'pickingLayoutLine' &&
      pickingObjectInfo.target === 'envelope-trim-or-extend-line' &&
      !pickingObjectInfo.repeat
    ) {
      result.push([
        'Pick a line to trim or extend to.',
        {
          key: 'envelope-trimming',
          variant: 'info',
          interactive: true,
          action: <FinishEnvelopeTrimmingButton />,
        },
      ]);
    }

    if (!inLayoutView && inMemberCuttingMode === 'sticky') {
      result.push([
        'Click a reference line to cut against.',
        {
          key: 'sticky-member-cutting',
          variant: 'info',
          interactive: true,
          action: <FinishMemberCuttingButton />,
        },
      ]);
    }

    if (analysisSet) {
      result.push(
        ...analysisSet.warnings.map((warning, i) =>
          createSelectorSnackbar(warning, `analysis-${analysisSet.guid}-warning-${i}`, 'warning'),
        ),
      );

      result.push(
        ...analysisSet.errors.map((error, i) =>
          createSelectorSnackbar(error, `analysis-${analysisSet.guid}-error-${i}`, 'error'),
        ),
      );
    }

    if (trussEnvelopeError) {
      const { message, documentationUrl } = trussEnvelopeError;
      result.push([
        message,
        {
          key: 'truss-envelope-error',
          variant: 'error',
          action: documentationUrl && <DocumentationLinkButton url={documentationUrl} />,
        },
      ]);
    }

    return result.map(([message, options]) => [message, { ...options, addedAt: Date.now(), persist: true }]);
  },
);

// this function purely exists to guide typescript into using the right type for a snackbar.
// without a type annotation of "Snackbar", typescript might consider this to be an array of a union type
function createSelectorSnackbar(message: string, key: string, variant: VariantType): SelectorSnackbar {
  return [message, { key, variant }];
}

const getNotificationToolsSnackbars = createSelector(
  (state: RootState) => state.notifications.snackbars,
  (notifications): Snackbar[] => notifications.map(createSnackbar),
);

export const getAllVisibleSnackbars = createSelector(
  getSelectorBasedSnackbars,
  getNotificationToolsSnackbars,
  (selectorBasedSnackbars, notificationToolsSnackbars): Snackbar[] => [
    ...selectorBasedSnackbars,
    ...notificationToolsSnackbars,
  ],
);

export const getNotificationCenterRelevantSnackbars = createSelector(getAllVisibleSnackbars, (snackbars) =>
  snackbars.filter(([, options]) => !options.interactive),
);

const variantsBySeverity = ['error', 'warning', 'info', 'default', 'success'] as const;

export function getWorstSeverityFromSnackbars(snackbars: Snackbar[]): VariantType {
  return (
    sortBy(uniq(snackbars.map(([, options]) => options.variant)), (variant) =>
      variantsBySeverity.indexOf(variant ?? 'default'),
    )[0] ?? 'default'
  );
}

export function sortSnackbarsBySeverity(snackbars: Snackbar[]): Snackbar[] {
  return sortBy(snackbars, ([, options]) => variantsBySeverity.indexOf(options.variant ?? 'default'));
}

export function toMuiSeverity(variant?: VariantType): AlertColor {
  variant ??= 'default';
  return variant === 'default' ? 'info' : variant;
}
